import moment from 'moment';

import type {
  ErpAiStudentFinancePortraitQuery,
  ErpAiStudentPortraitQuery,
} from 'gqlhooks';
import { PATHS } from 'router/config';
import { dashboardLink } from 'core/utils/dashboardDeepLink';
import { encode as encodeStudentLink } from 'core/utils/studentDeepLink';
import type { StudentTabId } from 'core/utils/studentAlerts';

/**
 * ТЗ 0104, §3.5 / §4 «Агент 3» — чистая модель ИИ-портрета студента:
 * статусы и опрос, тона серьёзности, коды отказов и сбоев, ссылки
 * доказательств. Без React — тестируется напрямую. Импортируется прямым
 * путём (`core/hooks/AiPortrait/aiPortraitModel`), не через барабан
 * `core/hooks`: там имена потоков столкнулись бы.
 */

// ── Типы ответа ────────────────────────────────────────────────────────────

export type AiStudentPortrait = ErpAiStudentPortraitQuery['aiStudentPortrait'];
export type AiPortraitItem = AiStudentPortrait['risks'][number];
export type AiEvidenceRef = AiPortraitItem['evidence'][number];
export type AiStudentFinancePortrait = NonNullable<
  ErpAiStudentFinancePortraitQuery['aiStudentFinancePortrait']
>;

// ── Статусы ────────────────────────────────────────────────────────────────

/** Статусы портрета (строки сервера — graphene-енумов нет). */
export const AI_PORTRAIT_STATUSES = [
  'none',
  'queued',
  'running',
  'ready',
  'failed',
] as const;
export type AiPortraitStatus = typeof AI_PORTRAIT_STATUSES[number];

export const isAiPortraitStatus = (value: unknown): value is AiPortraitStatus =>
  (AI_PORTRAIT_STATUSES as readonly unknown[]).includes(value);

/** Как часто портрет перечитывается, пока он в очереди или пишется. */
export const AI_PORTRAIT_POLL_MS = 4000;

/**
 * Портрет «в движении» — только по нему идёт опрос. `waiting_human` прогона
 * сервер показывает как `running`, отдельного статуса у портрета нет.
 */
export const aiPortraitPolled = (status: string | null | undefined): boolean =>
  status === 'queued' || status === 'running';

/**
 * Есть что показать: сводка, хоть один раздел или пункт. У `failed` и
 * `running` прежний портрет остаётся на экране — сервер отдаёт его поля
 * вместе с новым статусом.
 */
export const aiPortraitHasContent = (
  portrait: AiStudentPortrait | null | undefined,
): boolean =>
  Boolean(
    portrait &&
      (portrait.summary ||
        portrait.learning ||
        portrait.engagement ||
        portrait.sentimentTrend ||
        portrait.communication ||
        portrait.risks.length ||
        portrait.openNeeds.length ||
        portrait.nextActions.length),
  );

/** То же для финансового приложения. */
export const aiFinancePortraitHasContent = (
  finance: AiStudentFinancePortrait | null | undefined,
): boolean => Boolean(finance && (finance.summary || finance.items.length));

/**
 * Текст модели без разметки — для мини-карточки, где markdown не рисуется
 * (там три строки сводки): `**жирный**` → «жирный», `[текст](url)` →
 * «текст», маркеры заголовков, списков и цитат — прочь, переносы — в пробел.
 */
export const aiPortraitPlainText = (text: string | null | undefined): string =>
  (text ?? '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, '')
    .replace(/(\*\*|__|\*|`)/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// ── Разделы ────────────────────────────────────────────────────────────────

/** Текстовые разделы портрета — порядок на экране. */
export const AI_PORTRAIT_TEXT_SECTIONS = [
  'learning',
  'engagement',
  'sentimentTrend',
  'communication',
] as const;
export type AiPortraitTextSection = typeof AI_PORTRAIT_TEXT_SECTIONS[number];

/** Списки портрета — порядок на экране. */
export const AI_PORTRAIT_LISTS = ['risks', 'openNeeds', 'nextActions'] as const;
export type AiPortraitList = typeof AI_PORTRAIT_LISTS[number];

// ── Серьёзность ────────────────────────────────────────────────────────────

export const AI_PORTRAIT_SEVERITIES = ['high', 'normal', 'low'] as const;
export type AiPortraitSeverity = typeof AI_PORTRAIT_SEVERITIES[number];

export const isAiPortraitSeverity = (
  value: unknown,
): value is AiPortraitSeverity =>
  (AI_PORTRAIT_SEVERITIES as readonly unknown[]).includes(value);

/**
 * Тон метки серьёзности — из палитры `TagTone` ERP: `high` — красный,
 * `normal` — оранжевый, `low` — серый. Незнакомая или пустая — серый.
 */
export type AiPortraitTone = 'danger' | 'warn' | 'neutral';

export const aiPortraitSeverityTone = (
  severity: string | null | undefined,
): AiPortraitTone => {
  if (severity === 'high') return 'danger';
  if (severity === 'normal') return 'warn';
  return 'neutral';
};

/** Ранг для сортировки: `high` выше всех, без серьёзности — ниже всех. */
const severityRank = (severity: string | null | undefined): number => {
  const at = (AI_PORTRAIT_SEVERITIES as readonly string[]).indexOf(
    severity ?? '',
  );
  return at === -1 ? AI_PORTRAIT_SEVERITIES.length : at;
};

/**
 * Главный риск для мини-карточки — самый серьёзный; при равной серьёзности
 * первый в порядке модели. Нет рисков — `null`.
 */
export const aiPortraitTopRisk = (
  risks: ReadonlyArray<AiPortraitItem> | null | undefined,
): AiPortraitItem | null =>
  (risks ?? []).reduce<AiPortraitItem | null>(
    (best, item) =>
      best === null || severityRank(item.severity) < severityRank(best.severity)
        ? item
        : best,
    null,
  );

// ── Отказы и сбои ──────────────────────────────────────────────────────────

/**
 * Коды отказа «Odśwież portret» (`extensions.code`) — у каждого своя фраза
 * `aiPortrait.refusal.<код>`; незнакомый — `generic`.
 */
export const AI_PORTRAIT_REFUSALS = [
  'ai_disabled',
  'already_running',
  'budget_exceeded',
  'forbidden',
  'not_found',
] as const;
export type AiPortraitRefusal = typeof AI_PORTRAIT_REFUSALS[number];

export const aiPortraitRefusalKey = (code: string): string =>
  (AI_PORTRAIT_REFUSALS as readonly string[]).includes(code)
    ? `aiPortrait.refusal.${code}`
    : 'aiPortrait.refusal.generic';

/**
 * Код упавшего прогона (`error` при `status: failed`) — фраза
 * `aiPortrait.failure.<код>`; незнакомый или пустой — `generic`.
 */
export const AI_PORTRAIT_FAILURES = [
  'budget_exceeded',
  'ai_disabled',
  'not_found',
  'internal_error',
] as const;

export const aiPortraitFailureKey = (code: string | null | undefined): string =>
  code && (AI_PORTRAIT_FAILURES as readonly string[]).includes(code)
    ? `aiPortrait.failure.${code}`
    : 'aiPortrait.failure.generic';

// ── Доказательства ─────────────────────────────────────────────────────────

/** Виды ссылок-доказательств (`AiEvidenceRefType.kind`). */
export const AI_EVIDENCE_KINDS = [
  'message',
  'sms',
  'note',
  'task',
  'attendance',
  'mark',
  'survey',
  'agreement',
  'payment',
  'invoice',
  'funding',
] as const;
export type AiEvidenceKind = typeof AI_EVIDENCE_KINDS[number];

export const isAiEvidenceKind = (value: unknown): value is AiEvidenceKind =>
  (AI_EVIDENCE_KINDS as readonly unknown[]).includes(value);

/**
 * Что из ссылок открывается у этого сотрудника. Ссылка в маршрут, которого у
 * роли нет, — 404; такой чип рисуется простым текстом.
 */
export interface IAiEvidenceAccess {
  /** Таск-трекер (`/dashboard`): `TASK_TRACKER_ROLES`, у методиста его нет. */
  canOpenTasks: boolean;
  /** Вкладки карточки студента — флаги `useStudentPermissions`. */
  canSeeCommunicationTab: boolean;
  canSeeSurveysTab: boolean;
  canSeeAgreementsTab: boolean;
  canSeePaymentsTab: boolean;
  canSeeInvoicesTab: boolean;
}

/** Вкладка карточки студента: адрес из `PATHS` и кодировщика её состояния. */
export const aiStudentTabHref = (
  studentId: string,
  tab: StudentTabId,
): string => {
  const qs = encodeStudentLink({
    tab,
    paymentsAgreement: 'all',
    paymentsStatus: 'all',
  }).toString();
  const base = PATHS.studentDetail.replace(':id', studentId);
  return qs ? `${base}?${qs}` : base;
};

/**
 * Куда ведёт чип доказательства; `null` — внутренней ссылки нет (простой
 * текст).
 *
 *  - `message` / `sms` / `note` → вкладка «Общение» карточки студента: там
 *    переписка Slack и почты, SMS и заметки. Тред по id сообщения в
 *    Коммуникаторе не открыть — ему нужна группа, а её у ссылки нет;
 *  - `task` → карточка задачи в таск-трекере (режим «команда»: автор
 *    портрета не обязательно исполнитель, закрытые — тоже);
 *  - `attendance` / `mark` → карточка группы (`id` — группа). Маршрут карточки
 *    группы есть у всех ролей портрета;
 *  - `survey` / `agreement` / `payment` / `invoice` → своя вкладка карточки;
 *  - `funding` — простым текстом: у заявки дофинансирования нет своего
 *    адреса, а раздел у части ролей портрета закрыт.
 */
export const aiEvidenceHref = (
  ref: Pick<AiEvidenceRef, 'kind' | 'id'>,
  studentId: string,
  access: IAiEvidenceAccess,
): string | null => {
  const id = (ref.id ?? '').trim();
  switch (ref.kind) {
    case 'message':
    case 'sms':
    case 'note':
      return access.canSeeCommunicationTab
        ? aiStudentTabHref(studentId, 'communication')
        : null;
    case 'task':
      return id && access.canOpenTasks
        ? dashboardLink({
            view: 'tasks',
            taskMode: 'team',
            taskFilter: 'all',
            withClosed: true,
            task: id,
          })
        : null;
    case 'attendance':
    case 'mark':
      return id ? PATHS.groupDetail.replace(':id', id) : null;
    case 'survey':
      return access.canSeeSurveysTab
        ? aiStudentTabHref(studentId, 'surveys')
        : null;
    case 'agreement':
      return access.canSeeAgreementsTab
        ? aiStudentTabHref(studentId, 'agreements')
        : null;
    case 'payment':
      return access.canSeePaymentsTab
        ? aiStudentTabHref(studentId, 'payments')
        : null;
    case 'invoice':
      return access.canSeeInvoicesTab
        ? aiStudentTabHref(studentId, 'invoices')
        : null;
    default:
      return null;
  }
};

// ── Время ──────────────────────────────────────────────────────────────────

/** Дата и время портрета: «09.10.2026 14:05». */
export const formatAiPortraitDateTime = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : null;
};

/** Дата события доказательства: «01.10.2026». */
export const formatAiEvidenceDate = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : null;
};
